import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionButton, Buttons, CancelButton, Drawer, Icon, SingleSelect, Table, Tooltip } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';

export default function ClientMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { sageConnectionId } = useParams();

  const [{ clients, customers, isReady }, setState] = useState({ clients: null, isReady: false });

  const errorMappings = useMemo(() => {
    if (!clients) return {};
    return clients
      .filter((c) => c.sageCustomerId && clients.find((m) => m.sageCustomerId === c.sageCustomerId && m.id !== c.id))
      .reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
  }, [clients]);

  const usedSageCustomerIds = useMemo(
    () => clients?.filter((c) => c.sageCustomerId).map((m) => m.sageCustomerId) || [],
    [clients],
  );

  const hasErrors = useMemo(() => Object.values(errorMappings).some((v) => v), [errorMappings]);

  useEffect(() => {
    (async () => {
      const { data: clients } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getClientMappings();
      const { data: customers } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getCustomers();

      if (!isMounted.current) return;
      setState({ clients, customers, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const updateClientMapping = ({ clientId, sageCustomerId }) => {
    setDirty(true);
    setState((state) => ({
      ...state,
      clients: state.clients.map((c) => (c.id === clientId ? { ...c, sageCustomerId } : c)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Client Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();
          try {
            const mappings = clients.map((client) => ({
              clientId: client.id,
              sageCustomerId: client.sageCustomerId,
            }));
            await api.www.workspaces(workspace.id).sage(sageConnectionId).updateClientMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Client</Table.Column>
                <Table.Column>Sage Customer</Table.Column>
              </Table.BoxHeader>
              <Table.Body>
                {clients.map((client) => {
                  const handleCustomerChange = (event) => {
                    updateClientMapping({ clientId: client.id, sageCustomerId: event.target.value || null });
                  };
                  const availableCustomers = customers.filter(
                    (customer) => !usedSageCustomerIds.includes(customer.id) || customer.id === client.sageCustomerId,
                  );
                  const error = errorMappings[client.id]
                    ? { message: 'Customer already mapped to another client' }
                    : null;
                  return (
                    <Table.BoxRow key={client.id}>
                      <Table.Cell>{client.name}</Table.Cell>
                      <Table.Cell>
                        <CustomerSelect
                          customers={availableCustomers}
                          value={client.sageCustomerId}
                          onChange={handleCustomerChange}
                          error={error}
                        />
                      </Table.Cell>
                    </Table.BoxRow>
                  );
                })}
              </Table.Body>
            </Table>
            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave} disabled={hasErrors}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}

function CustomerSelect({ customers, value, error, ...props }) {
  const [filterValue, setFilterValue] = useState('');

  const options = useMemo(() => {
    if (!filterValue) return customers;
    const fuse = new Fuse(customers, { keys: ['name'], threshold: 0.1 });
    return fuse.search(filterValue).map((r) => r.item);
  }, [customers, filterValue]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const customer = _.find(customers, { id: value });
    if (!customer)
      return {
        id: value,
        name: (
          <Tooltip message="Customer not found in Sage Intacct.">
            <Icon icon="warning" color={colors.warning} spaceRight />
            Customer ID: {value}
          </Tooltip>
        ),
      };
    if (error)
      return {
        id: value,
        name: (
          <Tooltip message={error.message}>
            <Icon icon="exclamation-circle" color={colors.danger} spaceRight />
            {customer.name}
          </Tooltip>
        ),
      };
    return customer;
  }, [customers, value, error]);

  return (
    <SingleSelect
      showEmptyOption
      showFilter
      value={value}
      valueRenderer={selectedOption?.name ?? ''}
      onFilterChange={(event) => setFilterValue(event.target.value)}
      {...props}>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
        </option>
      ))}
    </SingleSelect>
  );
}
